import React, { useState, useMemo } from 'react';
import { Search, Leaf, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tree } from '../../types/tree';
import { NatureCard } from '../shared/NatureCard';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import { SpeciesCard } from './SpeciesCard';
import './EnhancedSpeciesTab.css';

interface EnhancedSpeciesTabProps {
  trees: Tree[];
}

export const EnhancedSpeciesTab: React.FC<EnhancedSpeciesTabProps> = ({ trees }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFamily, setSelectedFamily] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'count'>('count');

  // Calculate species data
  const speciesData = useMemo(() => {
    const speciesMap: Record<string, {
      count: number;
      family: string;
      native: boolean;
      endemic: boolean;
      invasive: boolean;
      avgGBH: number;
      avgHeight: number;
      avgBiomass: number;
      avgCarbon: number;
      avgCO2: number;
      sampleTree: Tree;
    }> = {};

    trees.forEach(tree => {
      if (!tree.Species) return;

      if (!speciesMap[tree.Species]) {
        speciesMap[tree.Species] = {
          count: 0,
          family: tree.Family || 'Unknown',
          native: Number(tree.Native) === 1,
          endemic: Number(tree.Endemism) === 1,
          invasive: Number(tree.Invasive) === 1,
          avgGBH: 0,
          avgHeight: 0,
          avgBiomass: 0,
          avgCarbon: 0,
          avgCO2: 0,
          sampleTree: tree,
        };
      }

      const species = speciesMap[tree.Species];
      species.count++;
      species.avgGBH += Number(tree.GBH) || 0;
      species.avgHeight += Number(tree.Height) || 0;
      species.avgBiomass += Number(tree.Biomass) || 0;
      species.avgCarbon += Number(tree.Carbon) || 0;
      species.avgCO2 += Number(tree.co2) || 0;
    });

    // Calculate averages
    Object.values(speciesMap).forEach(species => {
      species.avgGBH = species.avgGBH / species.count;
      species.avgHeight = species.avgHeight / species.count;
      species.avgBiomass = species.avgBiomass / species.count;
      species.avgCarbon = species.avgCarbon / species.count;
      species.avgCO2 = species.avgCO2 / species.count;
    });

    return speciesMap;
  }, [trees]);

  // Get unique families
  const families = useMemo(() => {
    const familySet = new Set(Object.values(speciesData).map(s => s.family));
    return ['all', ...Array.from(familySet).sort()];
  }, [speciesData]);

  // Filter and sort species
  const filteredSpecies = useMemo(() => {
    let filtered = Object.entries(speciesData);

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(([name]) =>
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by family
    if (selectedFamily !== 'all') {
      filtered = filtered.filter(([, data]) => data.family === selectedFamily);
    }

    // Filter by conservation status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(([, data]) => {
        switch (selectedStatus) {
          case 'native':
            return data.native && !data.endemic;
          case 'endemic':
            return data.endemic;
          case 'invasive':
            return data.invasive;
          case 'non-native':
            return !data.native && !data.invasive && !data.endemic;
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'count') {
        return b[1].count - a[1].count;
      } else {
        return a[0].localeCompare(b[0]);
      }
    });

    return filtered;
  }, [speciesData, searchTerm, selectedFamily, selectedStatus, sortBy]);

  // Conservation status distribution
  const conservationData = useMemo(() => {
    const native = Object.values(speciesData).filter(s => s.native).length;
    const endemic = Object.values(speciesData).filter(s => s.endemic).length;
    const invasive = Object.values(speciesData).filter(s => s.invasive).length;
    const nonNative = Object.values(speciesData).length - native - invasive;

    return [
      { name: 'Native', value: native, color: '#27ae60' },
      { name: 'Endemic', value: endemic, color: '#8e44ad' },
      { name: 'Invasive', value: invasive, color: '#e74c3c' },
      { name: 'Non-native', value: nonNative, color: '#3498db' },
    ].filter(d => d.value > 0);
  }, [speciesData]);

  // Family distribution (top 10)
  const familyData = useMemo(() => {
    const familyCount: Record<string, number> = {};
    Object.values(speciesData).forEach(species => {
      familyCount[species.family] = (familyCount[species.family] || 0) + species.count;
    });

    return Object.entries(familyCount)
      .map(([family, count]) => ({ family, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [speciesData]);

  // Top 10 Species by Average Carbon Storage
  const carbonStorageData = useMemo(() => {
    return Object.entries(speciesData)
      .map(([name, data]) => ({
        species: name.length > 30 ? name.substring(0, 27) + '...' : name,
        fullName: name,
        avgCO2: data.avgCO2,
        count: data.count,
      }))
      .sort((a, b) => b.avgCO2 - a.avgCO2)
      .slice(0, 10)
      .reverse(); // Reverse for horizontal chart (highest at top)
  }, [speciesData]);

  return (
    <section className="enhanced-species-tab">
      <div className="enhanced-species-tab__container">
        {/* Header */}
        <div className="enhanced-species-tab__header">
          <Leaf size={48} className="enhanced-species-tab__header-icon" />
          <div>
            <h2 className="enhanced-species-tab__title">Species Encyclopedia</h2>
            <p className="enhanced-species-tab__subtitle">
              Explore {Object.keys(speciesData).length} unique species across {families.length - 1} botanical families
            </p>
          </div>
        </div>

        <BotanicalDivider style="simple" spacing="medium" />

        {/* Stats Overview */}
        <div className="enhanced-species-tab__stats">
          <NatureCard className="enhanced-species-tab__stat-card" delay={0.1}>
            <div className="enhanced-species-tab__stat-icon" style={{ background: '#27ae60' }}>
              <Leaf size={28} />
            </div>
            <div className="enhanced-species-tab__stat-content">
              <div className="enhanced-species-tab__stat-value">{Object.keys(speciesData).length}</div>
              <div className="enhanced-species-tab__stat-label">Unique Species</div>
            </div>
          </NatureCard>

          <NatureCard className="enhanced-species-tab__stat-card" delay={0.2}>
            <div className="enhanced-species-tab__stat-icon" style={{ background: '#8e44ad' }}>
              <BarChart3 size={28} />
            </div>
            <div className="enhanced-species-tab__stat-content">
              <div className="enhanced-species-tab__stat-value">{families.length - 1}</div>
              <div className="enhanced-species-tab__stat-label">Botanical Families</div>
            </div>
          </NatureCard>

          <NatureCard className="enhanced-species-tab__stat-card" delay={0.3}>
            <div className="enhanced-species-tab__stat-icon" style={{ background: '#9b59b6' }}>
              <Leaf size={28} />
            </div>
            <div className="enhanced-species-tab__stat-content">
              <div className="enhanced-species-tab__stat-value">
                {Object.values(speciesData).filter(s => s.endemic).length}
              </div>
              <div className="enhanced-species-tab__stat-label">Endemic Species</div>
            </div>
          </NatureCard>

          <NatureCard className="enhanced-species-tab__stat-card" delay={0.4}>
            <div className="enhanced-species-tab__stat-icon" style={{ background: '#3498db' }}>
              <PieChartIcon size={28} />
            </div>
            <div className="enhanced-species-tab__stat-content">
              <div className="enhanced-species-tab__stat-value">
                {Object.values(speciesData).filter(s => s.native).length}
              </div>
              <div className="enhanced-species-tab__stat-label">Native Species</div>
            </div>
          </NatureCard>
        </div>

        {/* Charts Section */}
        <div className="enhanced-species-tab__charts">
          {/* Top 10 Species by Average Carbon Storage - Left Side */}
          <NatureCard className="enhanced-species-tab__chart-card enhanced-species-tab__chart-card--large">
            <h3 className="enhanced-species-tab__chart-title">Top 10 Species by Average Carbon Storage</h3>
            <p className="enhanced-species-tab__chart-description">
              Species with highest average carbon storage per tree (kg CO₂ per tree)
            </p>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={carbonStorageData}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  type="number"
                  label={{ value: 'Avg CO₂ (tonnes/tree)', position: 'insideBottom', offset: -10, style: { fill: '#6c757d' } }}
                  tick={{ fill: '#6c757d', fontSize: 12 }}
                />
                <YAxis
                  type="category"
                  dataKey="species"
                  tick={{ fill: '#6c757d', fontSize: 11 }}
                  width={140}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                  formatter={(value: number, name: string, props: any) => {
                    if (name === 'avgCO2') {
                      return [
                        `${value.toFixed(2)} kg CO₂/tree`,
                        `Average for ${props.payload.count} tree${props.payload.count > 1 ? 's' : ''}`
                      ];
                    }
                    return value;
                  }}
                  labelFormatter={(label: string, payload: any) => {
                    if (payload && payload.length > 0) {
                      return payload[0].payload.fullName;
                    }
                    return label;
                  }}
                />
                <Bar dataKey="avgCO2" fill="#2d5016" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </NatureCard>

          {/* Right Side - Stacked Charts */}
          <div className="enhanced-species-tab__charts-stack">
            {/* Conservation Status */}
            <NatureCard className="enhanced-species-tab__chart-card">
              <h3 className="enhanced-species-tab__chart-title">Conservation Status Distribution</h3>
              <p className="enhanced-species-tab__chart-description">
                Species breakdown by conservation significance
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={conservationData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 20;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);

                      if (percent < 0.05) return null;

                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#2c3e50"
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          fontSize={12}
                          fontWeight={500}
                        >
                          {`${(percent * 100).toFixed(0)}%`}
                        </text>
                      );
                    }}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {conservationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </NatureCard>

            {/* Family Distribution */}
            <NatureCard className="enhanced-species-tab__chart-card">
              <h3 className="enhanced-species-tab__chart-title">Top 10 Botanical Families</h3>
              <p className="enhanced-species-tab__chart-description">
                Most represented families by tree count
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={familyData} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="family"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: '#6c757d', fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: '#6c757d', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '12px',
                    }}
                  />
                  <Bar dataKey="count" fill="#4a7c59" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </NatureCard>
          </div>
        </div>

        <BotanicalDivider style="leaf" spacing="large" emoji="🌺" />

        {/* Search and Filter */}
        <div className="enhanced-species-tab__controls">
          <div className="enhanced-species-tab__search">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search species..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="enhanced-species-tab__search-input"
            />
          </div>

          <div className="enhanced-species-tab__filters">
            <select
              value={selectedFamily}
              onChange={(e) => setSelectedFamily(e.target.value)}
              className="enhanced-species-tab__select"
            >
              {families.map(family => (
                <option key={family} value={family}>
                  {family === 'all' ? 'All Families' : family}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="enhanced-species-tab__select"
            >
              <option value="all">All Status</option>
              <option value="native">Native</option>
              <option value="endemic">Endemic</option>
              <option value="invasive">Invasive</option>
              <option value="non-native">Non-native</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'count')}
              className="enhanced-species-tab__select"
            >
              <option value="count">Sort by Count</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="enhanced-species-tab__results-count">
          Showing {filteredSpecies.length} of {Object.keys(speciesData).length} species
        </div>

        {/* Species Grid */}
        <div className="enhanced-species-tab__grid">
          {filteredSpecies.map(([name, data], index) => (
            <SpeciesCard
              key={name}
              species={name}
              data={data}
              delay={index * 0.02}
            />
          ))}
        </div>

        {filteredSpecies.length === 0 && (
          <div className="enhanced-species-tab__no-results">
            <Leaf size={48} />
            <p>No species found matching your criteria</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default EnhancedSpeciesTab;
